import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import {
  MdCheck,
  MdClose,
  MdOutlineClearAll,
  MdOutlineDateRange,
  MdOutlineManageSearch,
  MdRadioButtonUnchecked,
  MdTaskAlt,
} from "react-icons/md";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import TextInput from "../TextInput/TextInput";
import { motion } from "framer-motion";
import { find } from "lodash-es";
import moment from "moment";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import useAutoComplete from "../../hooks/useAutoComplete";

import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import useCollapsibles from "../../hooks/useCollapsibles";
import CustomSelect from "../Select/CustomSelect";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useCurrentUser from "src/hooks/useCurrentUser";
import { objectHasChanges, transformToSelectOptions } from "src/helpers/utils";
import useAllTransactions from "src/hooks/useAllTransactions";
import useMainFilter from "src/hooks/useMainFilter";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface ITransactionsSearchModal {
  onClose: () => void;
  disableFields?: boolean;
  onSubmit: (values: any) => void;
}

const TransactionsSearchModal: React.FC<ITransactionsSearchModal> = ({
  onClose,
  disableFields = false,
  onSubmit,
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );

  const { t } = useTranslation(["modals", "common"]);
  const { allTagList } = useAutoComplete();
  const { mainFilter } = useMainFilter();
  const { isFilteringTransactions } = useFilteredTransactions();
  const { accountStatusesObject } = useCurrentAccount();
  const { allCategoriesObject, allSourcesObject } = useAllTransactions();
  const { allPaymentMethods, getPaymentMethodById } = useCurrentUser();
  const paymentMethods = useMemo(() => {
    return transformToSelectOptions({
      data: allPaymentMethods,
      labelField: "name",
      valueField: "id",
    });
  }, [allPaymentMethods]);

  const [selectedTags, setSelectedTags] = useState<Array<any>>(
    mainFilter?.tags?.map?.((tag: any) => ({ value: tag, label: tag })) || [],
  );
  const [selectedCategories, setSelectedCategories] = useState<Array<any>>(
    mainFilter?.category?.map?.((cat: any) => ({ value: cat, label: cat })) ||
      [],
  );
  const [selectedClientProviders, setSelectedClientProviders] = useState<
    Array<any>
  >(
    mainFilter?.clientProvider?.map?.((payTo: any) => ({
      value: payTo,
      label: payTo,
    })) || [],
  );
  // const [selectedStatuses, setSelectedStatuses] = useState<Array<any>>(
  //   mainFilter?.status?.map?.((status) => ({
  //     value: status,
  //     label: transactionStatus.getNameById(status),
  //   })) || [],
  // );
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    Array<any>
  >(
    mainFilter?.paymentMethod?.map?.((paymentMethod: any) => ({
      value: paymentMethod,
      label: getPaymentMethodById(paymentMethod)?.name,
    })) || [],
  );

  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [account, setAccount] = useState<string>("");
  const [payTo, setPayTo] = useState<Array<string>>([]);
  const [status, setStatus] = useState<Array<string>>([]);
  const [paymentMethod, setPaymentMethod] = useState<Array<string>>([]);
  const [tags, setTags] = useState<Array<string>>([]);
  const [category, setCategory] = useState<Array<string>>([]);

  const showClearFilters = () => {
    return (
      dateFrom ||
      dateTo ||
      category?.length ||
      payTo?.length ||
      status?.length ||
      paymentMethod?.length ||
      tags?.length
    );
  };

  const isTodaySelected = () => {
    const today = moment();

    return (
      dateFrom === today.format("yyyy-MM-DD") &&
      dateTo === today.format("yyyy-MM-DD")
    );
  };

  const isYesterdaySelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).subtract(1, "day").format("yyyy-MM-DD") &&
      dateTo === moment(today).subtract(1, "day").format("yyyy-MM-DD")
    );
  };

  const isLastSevenDaysSelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).subtract(1, "week").format("yyyy-MM-DD") &&
      dateTo === moment(today).format("yyyy-MM-DD")
    );
  };

  const isLast15DaysSelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).subtract(2, "week").format("yyyy-MM-DD") &&
      dateTo === moment(today).format("yyyy-MM-DD")
    );
  };

  const isLast22DaysSelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).subtract(3, "week").format("yyyy-MM-DD") &&
      dateTo === moment(today).format("yyyy-MM-DD")
    );
  };

  const isLast30DaysSelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).subtract(4, "week").format("yyyy-MM-DD") &&
      dateTo === moment(today).format("yyyy-MM-DD")
    );
  };

  const isThisMonthSelected = () => {
    const today = moment();

    return (
      dateFrom === moment(today).startOf("month").format("yyyy-MM-DD") &&
      dateTo === moment(today).endOf("month").format("yyyy-MM-DD")
    );
  };

  const isLastMonthSelected = () => {
    const today = moment();

    return (
      dateFrom ===
        moment(today)
          .subtract(1, "month")
          .startOf("month")
          .format("yyyy-MM-DD") &&
      dateTo ===
        moment(today).subtract(1, "month").endOf("month").format("yyyy-MM-DD")
    );
  };

  const dateOptions = useCallback(() => {
    const today = moment();

    return [
      {
        name: t("modals:search_transactions.collapsibles.default_ranges.today"),
        dateFrom: today.format("yyyy-MM-DD"),
        dateTo: today.format("yyyy-MM-DD"),
        active: isTodaySelected(),
      },
      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.yesterday",
        ),
        dateFrom: moment(today).subtract(1, "day").format("yyyy-MM-DD"),
        dateTo: moment(today).subtract(1, "day").format("yyyy-MM-DD"),
        active: isYesterdaySelected(),
      },
      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.last_7_days",
        ),
        dateFrom: moment(today).subtract(1, "week").format("yyyy-MM-DD"),
        dateTo: moment(today).format("yyyy-MM-DD"),
        active: isLastSevenDaysSelected(),
      },
      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.last_15_days",
        ),
        dateFrom: moment(today).subtract(2, "week").format("yyyy-MM-DD"),
        dateTo: moment(today).format("yyyy-MM-DD"),
        active: isLast15DaysSelected(),
      },
      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.last_22_days",
        ),
        dateFrom: moment(today).subtract(3, "week").format("yyyy-MM-DD"),
        dateTo: moment(today).format("yyyy-MM-DD"),
        active: isLast22DaysSelected(),
      },

      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.last_30_days",
        ),
        dateFrom: moment(today).subtract(4, "week").format("yyyy-MM-DD"),
        dateTo: moment(today).format("yyyy-MM-DD"),
        active: isLast30DaysSelected(),
      },

      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.current_month",
        ),
        dateFrom: moment(today).startOf("month").format("yyyy-MM-DD"),
        dateTo: moment(today).endOf("month").format("yyyy-MM-DD"),
        active: isThisMonthSelected(),
      },
      {
        name: t(
          "modals:search_transactions.collapsibles.default_ranges.last_month",
        ),
        dateFrom: moment(today)
          .subtract(1, "month")
          .startOf("month")
          .format("yyyy-MM-DD"),
        dateTo: moment(today)
          .subtract(1, "month")
          .endOf("month")
          .format("yyyy-MM-DD"),
        active: isLastMonthSelected(),
      },
    ];
  }, [
    dateFrom,
    dateTo,
    isLast15DaysSelected,
    isLast22DaysSelected,
    isLast30DaysSelected,
    isLastMonthSelected,
    isLastSevenDaysSelected,
    isTodaySelected,
    isYesterdaySelected,
    isThisMonthSelected,
    t,
  ]);

  const getActiveDateText = useMemo(() => {
    const activeOption = find(dateOptions(), { active: true });
    if (activeOption) {
      return activeOption?.name;
    }

    if (!dateFrom && !dateTo) {
      return t("modals:search_transactions.dates.all");
    }

    if (dateFrom && dateTo) {
      return t("modals:search_transactions.dates.custom");
    }

    return t("common:badges.incomplete");
  }, [dateOptions, dateFrom, dateTo]);

  useEffect(() => {
    setDateFrom(mainFilter.dateFrom);
    setDateTo(mainFilter.dateTo);
    setType(mainFilter.type);
    setStatus(mainFilter.status);
    setPaymentMethod(mainFilter.paymentMethod);
    setPayTo(mainFilter.clientProvider);
    setAccount(mainFilter.account);
    setTags(mainFilter.tags);
    setCategory(mainFilter.category);
  }, [mainFilter]);

  useEffect(() => {
    const simpleCatsArray = selectedCategories?.map?.((cat) => cat.value) || [];
    setCategory(simpleCatsArray);
  }, [selectedCategories]);

  useEffect(() => {
    const simpleClientProvidersArray =
      selectedClientProviders?.map?.(
        (clientProvider) => clientProvider.value,
      ) || [];
    setPayTo(simpleClientProvidersArray);
  }, [selectedClientProviders]);

  // useEffect(() => {
  //   const simpleStatusesArray =
  //     selectedStatuses?.map?.((status) => status.value) || [];
  //   setStatus(simpleStatusesArray);
  // }, [selectedStatuses]);

  useEffect(() => {
    const simplePaymentMethodsArray =
      selectedPaymentMethods?.map?.((paymentMethod) => paymentMethod.value) ||
      [];
    setPaymentMethod(simplePaymentMethodsArray);
  }, [selectedPaymentMethods]);

  useEffect(() => {
    const simpleTagsArray = selectedTags?.map?.((tag) => tag.value) || [];
    setTags(simpleTagsArray);
  }, [selectedTags]);

  const commonFieldsUsed = useMemo(() => {
    let total = 0;
    let selected = [];

    if (payTo?.length) {
      total++;
      selected.push("Origen / Destino");
    }
    // if (status?.length) {
    //   total++;
    //   selected.push("Estado");
    // }
    if (paymentMethod?.length) {
      total++;
      selected.push("Método de pago");
    }
    if (tags?.length) {
      total++;
      selected.push("Etiqueta");
    }
    if (category?.length) {
      total++;
      selected.push("Categoría");
    }

    let totalMessage = "";
    switch (total) {
      case 0: {
        totalMessage = t("modals:search_transactions.fields_count.all");
        break;
      }

      default: {
        totalMessage = t("modals:search_transactions.fields_count.count", {
          count: total,
        });
        break;
      }
    }

    return {
      totalMessage,
      selected: selected?.length ? selected?.join(", ") : null,
    };
  }, [mainFilter, category, payTo, paymentMethod]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        dateFrom: mainFilter.dateFrom,
        dateTo: mainFilter.dateTo,
        clientProvider: mainFilter.clientProvider,
        category: mainFilter.category,
      },
      edited: {
        dateFrom,
        dateTo,
        clientProvider: payTo,
        category,
      },
    });
  }, [mainFilter, dateFrom, dateTo, payTo, category]);

  return (
    <Modal
      onEscape={() => onClose()}
      className={clsx(["!w-full", "md:!min-w-[500px]", "md:!max-w-[500px]"])}
    >
      <Modal.Header
        onClose={() => onClose()}
        title={t("modals:search_transactions.title")}
        isClosable={!isFilteringTransactions}
      />
      <Modal.Body className={clsx(["!max-h-[60vh]", "md:h-auto"])}>
        <div className={clsx(["flex", "flex-col", "gap-4", "pb-2"])}>
          <div className={clsx(["flex", "flex-col", "gap-4"])}>
            <div
              className={clsx(
                "flex",
                "flex-col",
                "md:flex-col",
                "gap-4",
                "md:gap-6",
                "pb-2",
              )}
            >
              <CollapsibleBlock
                title={t("modals:search_transactions.collapsibles.date.title")}
                uppercase
                badge={getActiveDateText}
                // open={collapsiblePreferences.searchDatesOpen}
                blocked
                open
                onChange={(isOpen: boolean) => {
                  updateCollapsible({
                    searchDatesOpen: isOpen,
                  });
                }}
                icon={<MdOutlineDateRange size={ICON_SIZES.SM} />}
              >
                <div
                  className={clsx([
                    "flex",
                    "flex-col",
                    "w-full",
                    "gap-3",
                    "md:gap-4",
                  ])}
                >
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-2",
                      "md:grid-cols-2",
                      "gap-2",
                      "md:gap-4",
                    ])}
                  >
                    <TextInput
                      value={dateFrom}
                      onChange={(e: any) => setDateFrom(e?.target?.value)}
                      type="date"
                      label={t(
                        "modals:search_transactions.collapsibles.date.from",
                      )}
                      disabled={disableFields}
                      placeholder="dd/mm/aaaa"
                    />
                    <TextInput
                      value={dateTo}
                      onChange={(e: any) => setDateTo(e?.target?.value)}
                      type="date"
                      label={t(
                        "modals:search_transactions.collapsibles.date.to",
                      )}
                      disabled={disableFields}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                  <ul
                    className={clsx([
                      "grid",
                      "grid-cols-2",
                      "md:grid-cols-3",
                      "gap-1",
                      "md:gap-1",
                    ])}
                  >
                    {dateOptions().map((option, index) => {
                      return (
                        <motion.li
                          key={index}
                          className={clsx([
                            "group",
                            "whitespace-nowrap",
                            "cursor-pointer",
                            "transition-colors",
                            {
                              "hover:text-emerald-400 dark:hover:text-cyan-400":
                                !option.active,
                              "text-emerald-400 dark:text-cyan-500 md:dark:text-cyan-400 ":
                                option.active,
                            },
                            "flex",
                            "flex-row",
                            "gap-1",
                            "items-center",
                            "rounded",
                            "md:rounded-none",
                            "text-xs",
                            "md:text-sm",
                          ])}
                          onClick={() => {
                            const { dateFrom, dateTo, active } = option;

                            if (!active) {
                              setDateFrom(dateFrom);
                              setDateTo(dateTo);
                            } else {
                              setDateFrom("");
                              setDateTo("");
                            }
                          }}
                        >
                          <motion.div
                            key={option.active ? "active" : "inactive"}
                            // initial={{ scale: 0.8, opacity: 0 }}
                            {...(enableAnimations && {
                              initial: {
                                scale: option.active ? 0.8 : 1,
                                opacity: option.active ? 0 : 1,
                              },
                              animate: { scale: 1, opacity: 1 },
                              exit: { scale: 0.8, opacity: 0 },
                              transition: { duration: 0.2, ease: "easeInOut" },
                            })}
                          >
                            {option.active ? (
                              <MdTaskAlt
                                className={clsx([
                                  "md:flex",
                                  "text-emerald-400 dark:text-cyan-400",
                                  "shrink-0",
                                  "transition-colors",
                                ])}
                                size={ICON_SIZES.MD}
                              />
                            ) : (
                              <>
                                {option?.dateFrom && option?.dateTo && (
                                  <MdRadioButtonUnchecked
                                    className={clsx([
                                      "shrink-0",
                                      "transition-colors",
                                      "md:flex",
                                      "text-gray-300",
                                      "dark:text-purple-300",
                                      "group-hover:text-emerald-400 dark:group-hover:text-cyan-400",
                                    ])}
                                    size={ICON_SIZES.MD}
                                  />
                                )}
                              </>
                            )}
                          </motion.div>
                          {option.name}
                        </motion.li>
                      );
                    })}
                  </ul>
                </div>
              </CollapsibleBlock>

              <CollapsibleBlock
                title={t(
                  "modals:search_transactions.collapsibles.general.title",
                )}
                badge={commonFieldsUsed.totalMessage}
                // open={collapsiblePreferences.searchCommonFieldsOpen}
                blocked
                open
                onChange={(isOpen: boolean) => {
                  updateCollapsible({
                    searchCommonFieldsOpen: isOpen,
                  });
                }}
                icon={<MdOutlineManageSearch size={ICON_SIZES.SM} />}
              >
                <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-1",
                      "gap-2",
                      "md:gap-4",
                    ])}
                  >
                    <div
                      className={clsx(["flex", "w-full", "flex-col", "gap-1"])}
                    >
                      <CustomSelect
                        label={t(
                          "modals:search_transactions.collapsibles.general.category",
                        )}
                        onChange={(e: any) => {
                          setSelectedCategories(e);
                        }}
                        isMulti
                        hideSelectedOptions={false}
                        placeholder={t(
                          "modals:search_transactions.collapsibles.general.category_placeholder",
                        )}
                        value={selectedCategories}
                        useValueAsLabel={false}
                        options={allCategoriesObject}
                        withIcons
                        allowSearchModal
                        closeMenuOnSelect={false}
                      />
                    </div>

                    <div
                      className={clsx(["flex", "w-full", "flex-col", "gap-1"])}
                    >
                      <CustomSelect
                        label={t(
                          "modals:search_transactions.collapsibles.general.source",
                        )}
                        onChange={(e: any) => {
                          setSelectedClientProviders(e);
                        }}
                        isMulti
                        hideSelectedOptions={false}
                        placeholder={t(
                          "modals:search_transactions.collapsibles.general.source_placeholder",
                        )}
                        value={selectedClientProviders}
                        useValueAsLabel={false}
                        options={allSourcesObject}
                        withIcons
                        allowSearchModal
                        closeMenuOnSelect={false}
                      />
                    </div>

                    {/* <div
                      className={clsx(["flex", "w-full", "flex-col", "gap-1"])}
                    >
                      <CustomSelect
                        label={t(
                          "modals:search_transactions.collapsibles.general.status",
                        )}
                        onChange={(e: any) => {
                          setSelectedStatuses(e);
                        }}
                        isMulti
                        hideSelectedOptions={false}
                        placeholder={t(
                          "modals:search_transactions.collapsibles.general.status_placeholder",
                        )}
                        value={selectedStatuses}
                        useValueAsLabel={false}
                        options={accountStatusesObject}
                        isSearchable={false}
                        closeMenuOnSelect={false}
                      />
                    </div> */}
                    {/* <div
                      className={clsx(["flex", "w-full", "flex-col", "gap-1"])}
                    >
                      <CustomSelect
                        label={t(
                          "modals:search_transactions.collapsibles.general.payment_method",
                        )}
                        onChange={(e: any) => {
                          setSelectedPaymentMethods(e);
                        }}
                        isMulti
                        hideSelectedOptions={false}
                        placeholder={t(
                          "modals:search_transactions.collapsibles.general.payment_method_placeholder",
                        )}
                        value={selectedPaymentMethods}
                        useValueAsLabel={false}
                        options={paymentMethods}
                        allowSearchModal
                        closeMenuOnSelect={false}
                      />
                    </div> */}
                    {/* <div
                    className={clsx(["flex", "w-full", "flex-col", "gap-1"])}
                  >
                    <CustomSelect
                      label="Etiqueta"
                      onChange={(e: any) => {
                        setSelectedTags(e);
                      }}
                      isMulti
                      hideSelectedOptions={false}
                      placeholder="Todas las etiquetas"
                      value={selectedTags}
                      useValueAsLabel
                      options={
                        allTagList?.map?.((tag) => ({
                          value: tag,
                          label: tag,
                        })) as any
                      }
                    />
                  </div> */}
                  </div>
                </div>
              </CollapsibleBlock>
              {commonFieldsUsed?.selected?.length ? (
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "md:text-sm",
                    "text-sm",
                    "flex-col",
                    "mt-2",
                    "hidden",
                  ])}
                >
                  <div className="font-medium">Filtro activo</div>
                  <div>{commonFieldsUsed?.selected}</div>
                </div>
              ) : (
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "md:text-sm",
                    "text-sm",
                    "flex-col",
                    "mt-2",
                    "hidden",
                    "font-medium",
                  ])}
                >
                  Actualmente no tienes un filtro personalizado activo.
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common:buttons.apply"),
            onClick: (e: any) => {
              e?.preventDefault();
              onSubmit({
                dateFrom,
                dateTo,
                type,
                paymentMethod,
                account,
                clientProvider: payTo,
                // status,
                tags,
                category,
              });
            },
            icon: <MdCheck size={ICON_SIZES.SM} />,
            kind: EButtonKind.SAVE,
            disabled: isFilteringTransactions || !hasChanges,
            isLoading: isFilteringTransactions,
          },
          {
            text: t("common:buttons.close"),
            icon: <MdClose size={ICON_SIZES.SM} />,
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            disabled: isFilteringTransactions,
          },
        ]}
        leftContent={
          <>
            <ButtonGroup
              gap={1}
              align="left"
              buttons={[
                {
                  tooltip: t("common:tooltips.clear_fields"),
                  onClick: () => {
                    setDateFrom("");
                    setDateTo("");
                    setSelectedClientProviders([]);
                    // setSelectedStatuses([]);
                    setSelectedPaymentMethods([]);
                    setSelectedTags([]);
                    setSelectedCategories([]);
                  },
                  icon: <MdOutlineClearAll size={ICON_SIZES.SM} />,
                  kind: EButtonKind.DANGER,
                  hide: !showClearFilters() || isFilteringTransactions,
                  disabled: isFilteringTransactions,
                },
              ]}
            />
          </>
        }
      ></Modal.Footer>
    </Modal>
  );
};

export default TransactionsSearchModal;
